// Real backend client — talks to the live FastAPI server described by ./openapi.json.
//
// Functions here return the backend DTO shapes from ./schema (the source of
// truth). Endpoints that haven't been ported yet are still forwarded to the mock
// ./client via the `export *` below; the explicit `export`s in this file shadow
// the mock for every operation we've implemented, so the public surface that
// ../index.ts re-exports stays complete throughout the migration.

import type {
  RfqBuyerView,
  RfqSellerView,
  Buyer,
  OpenRfqDto,
  AwardQuoteChoiceDto,
  RfqQuoteDto,
  ProvisioningView,
  ProvisioningDetails,
  ProvisioningDetailsDefaults,
} from './schema'

import { getAuthHeader, AUTH_UNAUTHORIZED_EVENT } from '../auth/token'

// Forward every not-yet-implemented operation to the frozen mock client. Local
// `export`s of the same name below take precedence over these star re-exports.
export * from './client'

/**
 * Thin fetch wrapper: requests use same-origin relative paths (forwarded to the
 * backend by the Vite proxy in dev), attaches auth, JSON-encodes the request,
 * surfaces FastAPI's `detail` on errors, and tolerates the empty bodies that
 * 202/204 command responses return.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...getAuthHeader(),
      ...init.headers,
    },
  })

  if (!res.ok) throw await responseError(res, path, init.method ?? 'GET')

  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/**
 * Build the Error to throw for a non-OK response, shared by the JSON and binary
 * paths. Fires the unauthorized event on 401 (so the auth layer ends the
 * session) and surfaces FastAPI's `detail` (a string, or a validation array).
 */
async function responseError(res: Response, path: string, method: string): Promise<Error> {
  if (res.status === 401) window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT))

  let detail = `HTTP ${res.status}`
  try {
    const body = await res.json()
    if (typeof body?.detail === 'string') detail = body.detail
    else if (body?.detail != null) detail = JSON.stringify(body.detail)
  } catch { /* error body wasn't JSON — keep the status line */ }
  return new Error(`${method} ${path}: ${detail}`)
}

/** Parse the download filename out of a Content-Disposition header, if present. */
function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined
  // e.g. `attachment; filename="loa_abc_2.docx"` (also tolerates an unquoted name).
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  return match ? decodeURIComponent(match[1]) : undefined
}

/** Save a fetched Blob to disk by clicking a transient object-URL anchor. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// ── Buyer: RFQs ───────────────────────────────────────────────────────────────

/** GET /api/buyer/rfqs — every RFQ the current buyer has opened. */
export function getBuyerRFQs(): Promise<RfqBuyerView[]> {
  return request<RfqBuyerView[]>('/api/buyer/rfqs')
}

/** GET /api/buyer/rfqs/{rfq_id} — one RFQ, full buyer view. */
export function getBuyerRFQ(rfqId: string): Promise<RfqBuyerView> {
  return request<RfqBuyerView>(`/api/buyer/rfqs/${encodeURIComponent(rfqId)}`)
}

/** POST /api/buyer/rfqs/open — open a new RFQ on-chain. */
export function openRfq(rfq: OpenRfqDto): Promise<void> {
  return request<void>('/api/buyer/rfqs/open', {
    method: 'POST',
    body: JSON.stringify(rfq),
  })
}

/** POST /api/buyer/rfqs/{rfq_id}/award — award a winning seller per location. */
export function awardQuote(rfqId: number | string, choices: AwardQuoteChoiceDto[]): Promise<void> {
  return request<void>(`/api/buyer/rfqs/${rfqId}/award`, {
    method: 'POST',
    body: JSON.stringify(choices),
  })
}

/** POST /api/buyer/rfqs/{rfq_id}/decline — buyer rejects all quotes on the RFQ. */
export function declineRfqAsBuyer(rfqId: number | string): Promise<void> {
  return request<void>(`/api/buyer/rfqs/${rfqId}/decline`, { method: 'POST' })
}

// ── Seller: RFQs ──────────────────────────────────────────────────────────────

/** GET /api/seller/rfqs — every RFQ visible to the current seller. */
export function getSellerRFQs(): Promise<RfqSellerView[]> {
  return request<RfqSellerView[]>('/api/seller/rfqs')
}

/** GET /api/seller/rfqs/{rfq_id} — one RFQ, this seller's view. */
export function getSellerRFQ(rfqId: string): Promise<RfqSellerView> {
  return request<RfqSellerView>(`/api/seller/rfqs/${encodeURIComponent(rfqId)}`)
}

/** POST /api/seller/rfqs/{rfq_id}/quotes — submit this seller's quote. */
export function submitQuote(rfqId: number | string, quote: RfqQuoteDto): Promise<void> {
  return request<void>(`/api/seller/rfqs/${rfqId}/quotes`, {
    method: 'POST',
    body: JSON.stringify(quote),
  })
}

/** @deprecated Renamed to {@link submitQuote}; kept as an alias for call sites mid-migration. */
export const submitOrder = openRfq

/** POST /api/seller/rfqs/{rfq_id}/decline — seller declines to quote the RFQ. */
export function declineRfqAsSeller(rfqId: number | string): Promise<void> {
  return request<void>(`/api/seller/rfqs/${rfqId}/decline`, { method: 'POST' })
}

// ── Buyer profile ─────────────────────────────────────────────────────────────

/** GET /api/buyers/{buyer_id} — buyer profile (company, contacts, ASes). */
export function getBuyerDetails(buyerId: string): Promise<Buyer> {
  return request<Buyer>(`/api/buyers/${encodeURIComponent(buyerId)}`)
}

// ── Buyer: Provisioning ───────────────────────────────────────────────────────

/** GET /api/buyer/provisioning — every provisioning visible to the current buyer. */
export function getBuyerProvisionings(): Promise<ProvisioningView[]> {
  return request<ProvisioningView[]>('/api/buyer/provisioning')
}

/** GET /api/buyer/provisioning/{provisioning_id} — one provisioning, buyer view. */
export function getBuyerProvisioning(provisioningId: string): Promise<ProvisioningView> {
  return request<ProvisioningView>(
    `/api/buyer/provisioning/${encodeURIComponent(provisioningId)}`,
  )
}

/**
 * GET /api/buyer/provisioning/{provisioning_id}/default_provisioning_details —
 * the pre-fill defaults for this provisioning's submission form.
 */
export function getDefaultProvisioningDetails(
  provisioningId: string,
): Promise<ProvisioningDetailsDefaults> {
  return request<ProvisioningDetailsDefaults>(
    `/api/buyer/provisioning/${encodeURIComponent(provisioningId)}/default_provisioning_details`,
  )
}

/** POST /api/buyer/provisioning/{provisioning_id}/submission — submit provisioning details. */
export function submitProvisioningDetails(
  provisioningId: string,
  details: ProvisioningDetails,
): Promise<void> {
  return request<void>(
    `/api/buyer/provisioning/${encodeURIComponent(provisioningId)}/submission`,
    { method: 'POST', body: JSON.stringify(details) },
  )
}

/**
 * GET /api/buyer/provisioning/{provisioning_id}/submission/{submission_id}/loa —
 * download the Letter of Authorization (.docx) generated from a submission.
 *
 * The endpoint is auth-gated, so we fetch the bytes (rather than navigating to
 * the URL) and trigger a client-side save. The filename comes from the response's
 * Content-Disposition, falling back to the server's own naming scheme.
 */
export async function buyerDownloadProvisioningLoa(
  provisioningId: string,
  submissionId: number | string,
): Promise<void> {
  const path =
    `/api/buyer/provisioning/${encodeURIComponent(provisioningId)}` +
    `/submission/${encodeURIComponent(String(submissionId))}/loa`

  const res = await fetch(path, { headers: { ...getAuthHeader() } })
  if (!res.ok) throw await responseError(res, path, 'GET')

  const blob = await res.blob()
  const filename =
    filenameFromContentDisposition(res.headers.get('Content-Disposition')) ??
    `loa_${provisioningId}_${submissionId}.docx`

  saveBlob(blob, filename)
}

export async function sellerDownloadProvisioningLoa(
  provisioningId: string,
  submissionId: number | string,
): Promise<void> {
  const path =
    `/api/seller/provisioning/${encodeURIComponent(provisioningId)}` +
    `/submission/${encodeURIComponent(String(submissionId))}/loa`

  const res = await fetch(path, { headers: { ...getAuthHeader() } })
  if (!res.ok) throw await responseError(res, path, 'GET')

  const blob = await res.blob()
  const filename =
    filenameFromContentDisposition(res.headers.get('Content-Disposition')) ??
    `loa_${provisioningId}_${submissionId}.docx`

  saveBlob(blob, filename)
}


// ── Seller: Provisioning ──────────────────────────────────────────────────────

/** GET /api/seller/provisioning — every provisioning visible to the current seller. */
export function getSellerProvisionings(): Promise<ProvisioningView[]> {
  return request<ProvisioningView[]>('/api/seller/provisioning')
}

/** GET /api/seller/provisioning/{provisioning_id} — one provisioning, seller view. */
export function getSellerProvisioning(provisioningId: string): Promise<ProvisioningView> {
  return request<ProvisioningView>(
    `/api/seller/provisioning/${encodeURIComponent(provisioningId)}`,
  )
}

/**
 * POST /api/seller/provisioning/{provisioning_id}/submission/{submission_id}/acknowledge —
 * seller acknowledges receipt of a submission.
 */
export function acknowledgeProvisioningSubmission(
  provisioningId: string,
  submissionId: number | string,
): Promise<void> {
  return request<void>(
    `/api/seller/provisioning/${encodeURIComponent(provisioningId)}/submission/${encodeURIComponent(String(submissionId))}/acknowledge`,
    { method: 'POST' },
  )
}

/**
 * POST /api/seller/provisioning/{provisioning_id}/submission/{submission_id}/decline —
 * seller declines a submission with a reason (sent as the `reason` query param).
 */
export function declineProvisioningSubmission(
  provisioningId: string,
  submissionId: number | string,
  reason: string,
): Promise<void> {
  return request<void>(
    `/api/seller/provisioning/${encodeURIComponent(provisioningId)}/submission/${encodeURIComponent(String(submissionId))}/decline?reason=${encodeURIComponent(reason)}`,
    { method: 'POST' },
  )
}

/**
 * POST /api/seller/provisioning/{provisioning_id}/submission/{submission_id}/ready —
 * seller marks the provisioning ready.
 */
export function markProvisioningReady(
  provisioningId: string,
  submissionId: number | string,
): Promise<void> {
  return request<void>(
    `/api/seller/provisioning/${encodeURIComponent(provisioningId)}/submission/${encodeURIComponent(String(submissionId))}/ready`,
    { method: 'POST' },
  )
}

/**
 * POST /api/seller/provisioning/{provisioning_id}/submission/{submission_id}/fail —
 * seller marks the provisioning failed with a reason (sent as the `reason` query param).
 */
export function markProvisioningFailed(
  provisioningId: string,
  submissionId: number | string,
  reason: string,
): Promise<void> {
  return request<void>(
    `/api/seller/provisioning/${encodeURIComponent(provisioningId)}/submission/${encodeURIComponent(String(submissionId))}/fail?reason=${encodeURIComponent(reason)}`,
    { method: 'POST' },
  )
}
